import { useCallback, useState, useRef } from 'react'
import { useIsomorphicLayoutEffect, getScrollableParents, deepEqual } from './helpers'
import { computePosition, autoUpdate as autoUpdateFn } from './core'
import { TComputePositionConfigs, TContext, TOptions, TPositioningReturn, TReturnRefs, TPlacement } from './types'
import FloatingPortal from './FloatingPortal'

const useFloating = (options: TOptions): TPositioningReturn => {
    const { position = 'absolute', placement, config } = options
    const { autoUpdate = true } = config || {}

    const [_reference, _setReference] = useState<HTMLElement | null>(null)
    const [_popup, _setPopup] = useState<HTMLElement | null>(null)
    const referenceRef = useRef<HTMLElement | null>(null)
    const popupRef = useRef<HTMLElement | null>(null)
    const scrollableParents = useRef<Set<HTMLElement | Window>>(new Set<HTMLElement | Window>())

    const [context, setContext] = useState<TContext>({
        x: 0,
        y: 0,
        position,
        placement,
        isReferenceHidden: false
    })
    const contextRef = useRef<TContext>()

    /*
     * Custom function to avoid node being set multiple time
     * */
    const setReference = useCallback(
        (node: HTMLElement | null) => {
            if (node !== referenceRef.current) {
                referenceRef.current = node
                _setReference(node)
            }
        },
        [_setReference]
    )

    const setPopup = useCallback(
        (node: HTMLElement | null) => {
            if (node !== popupRef.current) {
                popupRef.current = node
                _setPopup(node)
            }
        },
        [_setPopup]
    )

    /*
     * Update function will be called to calculated popup position
     * */
    const update = useCallback(() => {
        if (!referenceRef.current || !popupRef.current) {
            return
        }
        const computePositionConfigs: TComputePositionConfigs = {
            placement,
            position,
            scrollableParents: scrollableParents.current,
            popupConfigs: config
        }

        const context = computePosition(referenceRef.current, popupRef.current, computePositionConfigs)
        if (!deepEqual(context, contextRef.current)) {
            contextRef.current = context
            setContext(context)
        }
    }, [placement, position, config])

    useIsomorphicLayoutEffect(() => {
        if (_reference) {
            referenceRef.current = _reference
        }
        if (_popup) {
            popupRef.current = _popup
        }
        if (_reference && _popup) {
            const target = position === 'fixed' ? _reference : _popup
            const scrollableParentsByTarget = getScrollableParents(target)
            if (scrollableParents.current.size === 0 && scrollableParentsByTarget.size > 0) {
                /*
                 * Get and store scrollableParents
                 * */
                scrollableParents.current = scrollableParentsByTarget
            }
            /*
             * Update function will calculate the position of popup element
             * */
            if (autoUpdate) {
                const clear = autoUpdateFn(scrollableParents.current, update)
                /*
                 * Observer the changes of popup element when auto update is enable
                 * */
                const observer = new MutationObserver(update)
                observer.observe(_popup, {
                    attributes: true,
                    childList: true,
                    subtree: true
                })
                return () => {
                    clear()
                    observer.disconnect()
                }
            }
            update()
        }
        return () => {}
    }, [_reference, _popup, update, position, autoUpdate])

    const refs: TReturnRefs = {
        setReference,
        setPopup,
        reference: referenceRef,
        popup: popupRef
    }

    return {
        refs,
        context,
        FloatingPortal
    }
}

export { TPlacement }

export default useFloating
